import React, { useState, useEffect } from 'react';
import { rolesAPI } from '../api/client';
import { Shield, Plus, Edit2, Trash2, X, Check, Users, Lock } from 'lucide-react';

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    nivel: 10,
    permisos: []
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [rolesRes, permisosRes] = await Promise.all([
        rolesAPI.getAll(),
        rolesAPI.getPermisos()
      ]);
      
      if (rolesRes.data.success) {
        setRoles(rolesRes.data.data);
      }
      
      if (permisosRes.data.success) {
        setPermisos(permisosRes.data.data);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar datos: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = async (rol = null) => {
    if (rol) {
      try {
        const response = await rolesAPI.getById(rol.id);
        if (response.data.success) {
          const rolData = response.data.data;
          setEditando(rolData);
          setFormData({
            id: rolData.id,
            nombre: rolData.nombre,
            descripcion: rolData.descripcion || '',
            nivel: rolData.nivel,
            permisos: rolData.permisos?.map(p => p.id) || []
          });
        }
      } catch (error) {
        console.error('Error al cargar rol:', error);
        alert('Error al cargar rol');
        return;
      }
    } else {
      setEditando(null);
      setFormData({
        nombre: '',
        descripcion: '',
        nivel: 10,
        permisos: []
      });
    }
    setShowModal(true);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setEditando(null);
    setFormData({
      nombre: '',
      descripcion: '',
      nivel: 10,
      permisos: []
    });
  };

  const togglePermiso = (permisoId) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.includes(permisoId)
        ? prev.permisos.filter(id => id !== permisoId)
        : [...prev.permisos, permisoId]
    }));
  };

  const toggleModulo = (modulo) => {
    const permisosModulo = permisos[modulo] || [];
    const idsModulo = permisosModulo.map(p => p.id);
    const todosSeleccionados = idsModulo.every(id => formData.permisos.includes(id));
    
    if (todosSeleccionados) {
      // Deseleccionar todos
      setFormData(prev => ({
        ...prev,
        permisos: prev.permisos.filter(id => !idsModulo.includes(id))
      }));
    } else {
      // Seleccionar todos
      setFormData(prev => ({
        ...prev,
        permisos: [...new Set([...prev.permisos, ...idsModulo])]
      }));
    }
  };

  const aplicarPlantilla = (tipo) => {
    const plantillas = {
      vendedor: ['ventas', 'productos', 'clientes', 'cotizaciones'],
      cajero: ['ventas', 'caja', 'clientes'],
      bodeguero: ['productos', 'inventario'],
      supervisor: ['ventas', 'productos', 'inventario', 'clientes', 'caja', 'reportes', 'cotizaciones'],
    };

    const modulosSeleccionados = plantillas[tipo] || [];
    const todosLosPermisos = [];

    modulosSeleccionados.forEach(modulo => {
      const permisosModulo = permisos[modulo] || [];
      const idsModulo = permisosModulo.map(p => p.id);
      todosLosPermisos.push(...idsModulo);
    });

    setFormData(prev => ({
      ...prev,
      permisos: [...new Set(todosLosPermisos)]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      alert('El nombre del rol es requerido');
      return;
    }

    try {
      setLoading(true);
      
      if (editando) {
        await rolesAPI.update(formData);
        alert('Rol actualizado exitosamente');
      } else {
        await rolesAPI.create(formData);
        alert('Rol creado exitosamente');
      }
      
      cerrarModal();
      cargarDatos();
    } catch (error) {
      console.error('Error al guardar rol:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const eliminarRol = async (id) => {
    if (!window.confirm('¿Estás seguro de desactivar este rol?')) {
      return;
    }

    try {
      await rolesAPI.delete(id);
      alert('Rol desactivado exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar rol:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const getRolBadgeColor = (nivel) => {
    if (nivel >= 100) return 'bg-purple-100 text-purple-800 border-purple-300';
    if (nivel >= 80) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (nivel >= 60) return 'bg-indigo-100 text-indigo-800 border-indigo-300';
    if (nivel >= 40) return 'bg-green-100 text-green-800 border-green-300';
    if (nivel >= 30) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getModuloIcon = (modulo) => {
    const icons = {
      ventas: '💰',
      productos: '📦',
      inventario: '📊',
      clientes: '👥',
      caja: '💵',
      reportes: '📈',
      configuracion: '⚙️',
      cotizaciones: '📋'
    };
    return icons[modulo] || '📄';
  };

  if (loading && roles.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-8 h-8 text-primary-600" />
              Roles y Permisos
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestiona roles y permisos del sistema
            </p>
          </div>
          <button
            onClick={() => abrirModal()}
            className="btn-primary flex items-center gap-2 justify-center"
          >
            <Plus className="w-5 h-5" />
            Nuevo Rol
          </button>
        </div>
      </div>

      {/* Lista de Roles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((rol) => (
          <div 
            key={rol.id} 
            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <Shield className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{rol.nombre}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full border ${getRolBadgeColor(rol.nivel)}`}>
                    Nivel {rol.nivel}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => abrirModal(rol)}
                  className="text-primary-600 hover:text-primary-900 p-1"
                  title="Editar"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {rol.nivel < 100 && (
                  <button
                    onClick={() => eliminarRol(rol.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {rol.descripcion && (
              <p className="text-sm text-gray-600 mb-4">{rol.descripcion}</p>
            )}
            
            <div className="flex items-center gap-4 text-sm text-gray-500 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{rol.usuarios_count || 0} usuarios</span>
              </div>
              <div className="flex items-center gap-1">
                <Lock className="w-4 h-4" />
                <span>{rol.permisos_count || 0} permisos</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {roles.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay roles</h3>
          <p className="mt-1 text-sm text-gray-500">Comienza creando un nuevo rol</p>
        </div>
      )}

      {/* Modal Crear/Editar Rol */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-primary-600" />
                  {editando ? 'Editar Rol' : 'Nuevo Rol'}
                </h2>
                <button
                  onClick={cerrarModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Información Básica */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Rol <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="input-field"
                    placeholder="Ej: Supervisor de Ventas"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nivel (1-100)
                  </label>
                  <input
                    type="number"
                    value={formData.nivel}
                    onChange={(e) => setFormData({...formData, nivel: parseInt(e.target.value)})}
                    className="input-field"
                    min="1"
                    max="100"
                    required
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                    className="input-field"
                    rows="2"
                    placeholder="Describe las responsabilidades de este rol..."
                  />
                </div>
              </div>

              {/* Permisos */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-primary-600" />
                    Seleccionar Permisos
                  </h3>
                  <div className="text-sm">
                    <span className="font-semibold text-primary-600">{formData.permisos.length}</span>
                    <span className="text-gray-600"> permisos seleccionados</span>
                  </div>
                </div>

                {/* Plantillas Predefinidas */}
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">🎯 Plantillas Rápidas</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <button
                      type="button"
                      onClick={() => aplicarPlantilla('vendedor')}
                      className="p-3 bg-white rounded-lg border-2 border-green-300 hover:border-green-500 hover:bg-green-50 transition-all text-center shadow-sm"
                    >
                      <div className="text-2xl mb-1">👨‍💼</div>
                      <div className="text-xs font-bold text-green-700">Vendedor</div>
                      <div className="text-xs text-gray-600 mt-1">Ventas + Clientes</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => aplicarPlantilla('cajero')}
                      className="p-3 bg-white rounded-lg border-2 border-yellow-300 hover:border-yellow-500 hover:bg-yellow-50 transition-all text-center shadow-sm"
                    >
                      <div className="text-2xl mb-1">💵</div>
                      <div className="text-xs font-bold text-yellow-700">Cajero</div>
                      <div className="text-xs text-gray-600 mt-1">Ventas + Caja</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => aplicarPlantilla('bodeguero')}
                      className="p-3 bg-white rounded-lg border-2 border-orange-300 hover:border-orange-500 hover:bg-orange-50 transition-all text-center shadow-sm"
                    >
                      <div className="text-2xl mb-1">📦</div>
                      <div className="text-xs font-bold text-orange-700">Bodeguero</div>
                      <div className="text-xs text-gray-600 mt-1">Inventario</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => aplicarPlantilla('supervisor')}
                      className="p-3 bg-white rounded-lg border-2 border-purple-300 hover:border-purple-500 hover:bg-purple-50 transition-all text-center shadow-sm"
                    >
                      <div className="text-2xl mb-1">👔</div>
                      <div className="text-xs font-bold text-purple-700">Supervisor</div>
                      <div className="text-xs text-gray-600 mt-1">Todo menos Config</div>
                    </button>
                  </div>
                </div>

                {/* Selección Rápida por Módulos */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">✨ Selección Rápida por Módulo</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.keys(permisos).map((modulo) => {
                      const permisosModulo = permisos[modulo] || [];
                      const idsModulo = permisosModulo.map(p => p.id);
                      const todosSeleccionados = idsModulo.every(id => formData.permisos.includes(id));
                      
                      return (
                        <button
                          key={modulo}
                          type="button"
                          onClick={() => toggleModulo(modulo)}
                          className={`p-3 rounded-lg border-2 transition-all text-center ${
                            todosSeleccionados
                              ? 'bg-primary-600 border-primary-600 text-white shadow-md'
                              : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400 hover:bg-primary-50'
                          }`}
                        >
                          <div className="text-2xl mb-1">{getModuloIcon(modulo)}</div>
                          <div className="text-xs font-semibold capitalize">{modulo}</div>
                          <div className="text-xs opacity-75 mt-1">
                            {idsModulo.filter(id => formData.permisos.includes(id)).length}/{idsModulo.length}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {Object.keys(permisos).map((modulo) => {
                    const permisosModulo = permisos[modulo] || [];
                    const idsModulo = permisosModulo.map(p => p.id);
                    const todosSeleccionados = idsModulo.every(id => formData.permisos.includes(id));
                    const algunoSeleccionado = idsModulo.some(id => formData.permisos.includes(id));

                    return (
                      <div key={modulo} className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary-200 transition-colors">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-bold text-gray-900 capitalize flex items-center gap-2 text-base">
                            <span className="text-3xl">{getModuloIcon(modulo)}</span>
                            {modulo}
                          </h4>
                          <button
                            type="button"
                            onClick={() => toggleModulo(modulo)}
                            className={`px-4 py-2 text-sm font-semibold rounded-lg border-2 transition-all shadow-sm hover:shadow ${
                              todosSeleccionados
                                ? 'bg-primary-600 border-primary-600 text-white hover:bg-primary-700'
                                : algunoSeleccionado
                                ? 'bg-yellow-500 border-yellow-500 text-white hover:bg-yellow-600'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {todosSeleccionados ? '✓ Todos' : algunoSeleccionado ? `${idsModulo.filter(id => formData.permisos.includes(id)).length} de ${idsModulo.length}` : 'Seleccionar Todos'}
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {permisosModulo.map((permiso) => {
                            const isSelected = formData.permisos.includes(permiso.id);
                            return (
                              <button
                                key={permiso.id}
                                type="button"
                                onClick={() => togglePermiso(permiso.id)}
                                className={`text-left p-3 rounded-lg border-2 transition-all ${
                                  isSelected
                                    ? 'bg-primary-50 border-primary-500 shadow-sm'
                                    : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-colors ${
                                    isSelected
                                      ? 'bg-primary-600 border-primary-600'
                                      : 'bg-white border-gray-300'
                                  }`}>
                                    {isSelected && (
                                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className={`text-sm font-semibold ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                      {permiso.nombre}
                                    </p>
                                    {permiso.descripcion && (
                                      <p className="text-xs text-gray-600 mt-1">{permiso.descripcion}</p>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="flex-1 btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : (editando ? 'Actualizar Rol' : 'Crear Rol')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPage;
